import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { CircleNotch, Plus, Receipt, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import InvoiceModal from "./InvoiceModal";

const TAB_TYPES = [
  { key: "racun", label: "RAČUNI" },
  { key: "avans", label: "AVANSI" },
  { key: "ponuda", label: "PONUDE" },
];

const TYPE_LABELS = {
  racun: "Račun",
  avans: "Avans",
  ponuda: "Ponuda",
  storno: "Storno",
};

export default function InvoicesTab({ reservationId, paymentStatus, onReservationSync }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("racun");
  const [openInvoiceId, setOpenInvoiceId] = useState(null);
  const [createType, setCreateType] = useState(null); // when creating a new invoice
  const [markingAll, setMarkingAll] = useState(false);

  const load = async () => {
    if (!reservationId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/invoices?reservation_id=${reservationId}`);
      setInvoices(data);
    } finally {
      setLoading(false);
    }
  };

  // Backend keeps reservation.payment_status mirroring these invoices' `paid`
  // state automatically — this just pulls that one field back into the
  // drawer header toggle so it never looks stale without closing/reopening.
  // Intentionally narrow (payment_status only) so it can never clobber any
  // unsaved edits the user has made elsewhere in the open reservation form.
  const syncReservation = async () => {
    if (!reservationId || !onReservationSync) return;
    try {
      const { data } = await api.get(`/reservations/${reservationId}`);
      onReservationSync({ payment_status: data.payment_status });
    } catch { /* non-fatal */ }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationId, paymentStatus]);

  // Group by tab type; storno is grouped with racun (since it lives beside its original)
  const filtered = useMemo(() => {
    return invoices.filter((inv) => {
      if (tab === "racun") return inv.invoice_type === "racun" || inv.invoice_type === "storno";
      return inv.invoice_type === tab;
    });
  }, [invoices, tab]);

  const counts = useMemo(() => {
    const c = { racun: 0, avans: 0, ponuda: 0 };
    invoices.forEach((inv) => {
      if (inv.invoice_type === "racun" || inv.invoice_type === "storno") c.racun += 1;
      else if (c[inv.invoice_type] != null) c[inv.invoice_type] += 1;
    });
    return c;
  }, [invoices]);

  const allPaid = filtered.length > 0 && filtered.every((inv) => inv.paid || inv.cancelled);

  const togglePaid = async (inv, next) => {
    try {
      await api.put(`/invoices/${inv.id}`, { paid: next });
      setInvoices((prev) => prev.map((i) => (i.id === inv.id ? { ...i, paid: next } : i)));
      syncReservation();
    } catch (e) {
      toast.error("Greška");
    }
  };

  const markAllPaid = async (next) => {
    setMarkingAll(true);
    try {
      const targets = filtered.filter((inv) => !inv.cancelled && !!inv.paid !== next);
      await Promise.all(
        targets.map((inv) => api.put(`/invoices/${inv.id}`, { paid: next })),
      );
      await load();
      syncReservation();
    } catch (e) {
      toast.error("Greška pri masovnom označavanju");
    } finally {
      setMarkingAll(false);
    }
  };

  const startCreate = () => {
    setCreateType(tab);
  };

  const modalClose = () => {
    setOpenInvoiceId(null);
    setCreateType(null);
    load();
    syncReservation();
  };

  return (
    <div className="flex h-full flex-col bg-white" data-testid="res-tab-invoices-content">
      {/* Tabs */}
      <div className="flex items-center gap-6 border-b border-slate-200 px-6 pt-4">
        {TAB_TYPES.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`inv-subtab-${t.key}`}
            className={`relative flex items-center gap-2 pb-2 text-sm font-bold uppercase tracking-wide transition ${
              tab === t.key
                ? "text-sky-500"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {t.label}
            {counts[t.key] > 0 && (
              <span className="rounded-full bg-sky-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                {counts[t.key]}
              </span>
            )}
            {tab === t.key && (
              <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-sky-500" />
            )}
          </button>
        ))}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        {loading ? (
          <div className="flex justify-center py-16">
            <CircleNotch size={22} className="animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Receipt size={56} weight="thin" className="text-slate-300" />
            <p className="mt-3 text-sm text-slate-500">
              Nema {tab === "racun" ? "računa" : tab === "avans" ? "avansa" : "ponuda"}
            </p>
            <button
              onClick={startCreate}
              className="mt-6 flex items-center gap-2 rounded bg-sky-500 px-6 py-2.5 text-sm font-bold uppercase tracking-wide text-white shadow hover:bg-sky-600"
              data-testid={`inv-empty-create-${tab}`}
            >
              <Plus size={14} weight="bold" /> {TYPE_LABELS[tab]}
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-md border border-slate-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-3 py-2 text-left font-semibold">Datum</th>
                    <th className="px-3 py-2 text-left font-semibold">Oznaka</th>
                    <th className="px-3 py-2 text-left font-semibold">Tip</th>
                    <th className="px-3 py-2 text-left font-semibold">Klijent</th>
                    <th className="px-3 py-2 text-right font-semibold">Količina</th>
                    <th className="px-3 py-2 text-center font-semibold">Plaćen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((inv) => (
                    <InvoiceRow
                      key={inv.id}
                      inv={inv}
                      onOpen={() => setOpenInvoiceId(inv.id)}
                      onTogglePaid={togglePaid}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-5 flex items-center justify-between">
              <button
                onClick={startCreate}
                className="flex items-center gap-2 rounded bg-sky-500 px-6 py-2.5 text-sm font-bold uppercase tracking-wide text-white shadow hover:bg-sky-600"
                data-testid={`inv-create-${tab}`}
              >
                <Plus size={14} weight="bold" /> {TYPE_LABELS[tab]}
              </button>
              <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500" title="Označi sve dokumente kao plaćene">
                Označi sve kao plaćeno
                <button
                  type="button"
                  onClick={() => markAllPaid(!allPaid)}
                  disabled={markingAll}
                  className={`relative h-5 w-9 rounded-full transition ${
                    allPaid ? "bg-emerald-500" : "bg-slate-300"
                  }`}
                  data-testid="inv-mark-all-paid"
                >
                  <span
                    className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${
                      allPaid ? "left-4" : "left-0.5"
                    }`}
                  />
                </button>
              </label>
            </div>
          </>
        )}
      </div>

      {/* Modal */}
      {(openInvoiceId || createType) && (
        <InvoiceModal
          invoiceId={openInvoiceId}
          invoiceType={createType}
          reservationId={reservationId}
          onClose={modalClose}
          onSaved={() => { load(); syncReservation(); }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
function InvoiceRow({ inv, onOpen, onTogglePaid }) {
  const isDraft = inv.status === "draft";
  const label = inv.cancelled ? "Storniran" : TYPE_LABELS[inv.invoice_type];
  return (
    <tr
      className={`cursor-pointer transition hover:bg-slate-50 ${inv.cancelled ? "opacity-50" : ""}`}
      onClick={onOpen}
      data-testid={`inv-row-${inv.id}`}
    >
      <td className="px-3 py-2.5 text-slate-700">{fmtDate(inv.issue_date)}</td>
      <td className="px-3 py-2.5 font-mono font-semibold text-slate-800">
        {isDraft ? (
          <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-2 py-0.5 text-[11px] font-bold uppercase text-amber-700">
            <PencilSimple size={10} weight="bold" /> Priprema
          </span>
        ) : (
          inv.number
        )}
      </td>
      <td className="px-3 py-2.5 text-slate-700">{label}</td>
      <td className="px-3 py-2.5 text-slate-700">{inv.client_name}</td>
      <td className="px-3 py-2.5 text-right font-mono font-semibold text-slate-800">
        {Number(inv.total || 0).toFixed(2)} {inv.currency}
      </td>
      <td className="px-3 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={() => onTogglePaid(inv, !inv.paid)}
          disabled={inv.cancelled}
          className={`relative inline-block h-5 w-9 rounded-full transition ${
            inv.paid ? "bg-emerald-500" : "bg-slate-300"
          } ${inv.cancelled ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}
          data-testid={`inv-toggle-paid-${inv.id}`}
          title={inv.paid ? "Plaćen" : "Neplaćen"}
        >
          <span
            className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${
              inv.paid ? "left-4" : "left-0.5"
            }`}
          />
        </button>
      </td>
    </tr>
  );
}

function fmtDate(iso) {
  if (!iso || iso.length < 10) return iso || "";
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;
}
